import React from 'react';
import { inr, pct } from './portfolioHealthCheckApi';

const STRENGTH = { clear: 'Clear signal', indicative: 'Indicative', limited: 'Limited evidence' };

/** Renders the server's health report verbatim. No wording or verdict is created in the browser. */
export default function HealthCheckReport({ health, links = {} }) {
  const { report: d, refNames, unassessed } = health;
  const label = (ref) => refNames[ref] || null;
  return (
    <div className="phc-report" data-verdict={d.verdict.kind}>
      {d.verdict.kind === 'incomplete' ? (
        <div className="phc-verdict phc-verdict--incomplete">
          <p className="phc-eyebrow">Check incomplete · partial comparison only</p>
          <h2 className="phc-verdict__h">{d.verdict.headline}</h2>
          <p>{d.verdict.summary}</p>
        </div>
      ) : (
        <div className="phc-verdict">
          <p className="phc-eyebrow phc-eyebrow--light" style={{ color: "white" }}>
            {d.coverage && d.coverage.state === 'qualified'
              ? 'Qualified Health Check · limited company coverage'
              : 'What we found'}
          </p>

          <h2 className="phc-verdict__h" style={{ color: "#fff" }}>
            {d.verdict.headline}
          </h2>

          <p style={{ color: "#fff" }}>
            {d.verdict.summary}
          </p>
        </div>
      )}

      {d.structure && d.verdict.kind === 'incomplete' && (
        <dl className="phc-stats phc-stats--boxed">
          <div><dt>Equity & hybrid funds</dt><dd>{d.structure.eligible_funds}</dd></div>
          <div><dt>Their current value</dt><dd>{inr(d.structure.eligible_value)}</dd></div>
          <div><dt>Largest {Math.min(5, d.structure.eligible_funds)} funds</dt><dd>{pct(d.structure.top_five_share)}</dd></div>
          <div><dt>Largest · smallest</dt><dd>{pct(d.structure.largest_share)} · {pct(d.structure.smallest_share)}</dd></div>
        </dl>
      )}

      {d.coverage && d.coverage.state !== 'complete' && (
        <div className="phc-coverage">
          <p className="phc-h3">Company holdings coverage</p>
          <p>{inr(d.coverage.covered_value)} of {inr(d.coverage.eligible_value)} ({pct(d.coverage.covered_share)}) has company holdings · {d.coverage.covered_funds} of {d.coverage.eligible_funds} funds.</p>
          <span className="phc-bar" aria-hidden="true"><span style={{ width: `${Math.min(100, (d.coverage.covered_share || 0) * 100)}%` }} /></span>
          {d.coverage.missing.length > 0 && <ul className="phc-list">{d.coverage.missing.map((m) => <li key={m.ref} className="phc-row"><span>{label(m.ref) || 'Identified fund'} — holdings not yet available</span><span className="phc-num">{inr(m.current)} · {pct(m.share_of_eligible)}</span></li>)}</ul>}
          {unassessed.length > 0 && <ul className="phc-list">{unassessed.map((u, i) => <li key={i} className="phc-row"><span>“{u.name}” — fund not confirmed, counted as possibly equity</span><span className="phc-num">{inr(u.value)}</span></li>)}</ul>}
          {d.coverage.disclosure_dates.length > 0 && <p className="phc-small">Disclosures as of {d.coverage.disclosure_dates.join(', ')}.</p>}
        </div>
      )}

      {d.gaps.length > 0 && (
        <ul className="phc-gaps" aria-label="What is missing">
          {d.gaps.map((g) => <li key={g.id}><p className="phc-strong">{g.title}</p><p className="phc-small">{g.detail}</p><p><span className="phc-strong">What you can do: </span>{g.action}</p></li>)}
        </ul>
      )}

      {d.takeaways.length > 0 && (
        <ol className="phc-takeaways" aria-label="What deserves attention, in order">
          {d.takeaways.map((t, i) => {
            const named = t.refs.map(label).filter(Boolean);
            return (
              <li key={t.id} className="phc-takeaway">
                <div className="phc-takeaway__meta"><span className="phc-rank">{i + 1}</span><span className="phc-chip">{STRENGTH[t.strength]}</span></div>
                <h3 className="phc-takeaway__h">{t.title}</h3>
                <p>{t.finding}</p>
                {named.length > 0 && <p className="phc-small">{named.slice(0, 6).join(' · ')}{named.length > 6 ? ` · +${named.length - 6} more` : ''}</p>}
                <p className="phc-muted">{t.why}</p>
                {t.evidence.length > 0 && <dl className="phc-evidence">{t.evidence.map((e) => <div key={e.label}><dt>{e.label}</dt><dd>{e.value}</dd></div>)}</dl>}
                <p><span className="phc-strong">Worth deciding next: </span>{t.next}</p>
              </li>
            );
          })}
        </ol>
      )}

      <div className="phc-scope">
        <p className="phc-small">Scope: {d.scope.eligible_funds} equity & hybrid funds worth {inr(d.scope.eligible_value)}; {inr(d.scope.excluded_value)} in other fund types is outside this check{d.scope.pending_lines ? `; ${d.scope.pending_lines} line(s) still unconfirmed` : ''}{d.scope.values_complete ? '' : '; some current values are missing'}.</p>
        <div className="phc-links">
          {d.next_paths.includes('portfolio_review') && links.portfolioReview && <a href={links.portfolioReview}>Discuss a portfolio review</a>}
          {d.next_paths.includes('overlap_article') && <a href={links.overlapArticle || 'https://www.finedge.in/blog/portfolio-review/mutual-fund-portfolio-overlap'} target="_blank" rel="noreferrer">When overlap matters</a>}
          {d.next_paths.includes('sif_portfolio_fit') && <a href={links.sif || 'https://www.finedge.in/sif-calculator'} target="_blank" rel="noreferrer">SIF Calculator</a>}
        </div>
        <p className="phc-small">Structural reading of fund holdings and values only — not an assessment of goals or risk, and not a recommendation to buy, sell or switch. FinEdge is an AMFI-registered Mutual Fund & SIF Distributor (ARN 83676).{d.scope.synthetic ? ' Synthetic example data.' : ''}</p>
      </div>
    </div>
  );
}
